import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  School as SchoolIcon,
  Assignment as AssignmentIcon,
  PendingActions as PendingActionsIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

function TeacherResultsEntry() {
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedExam, setSelectedExam] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedExamSubjectId, setSelectedExamSubjectId] = useState('');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [marksObtained, setMarksObtained] = useState('');
  const [isAbsent, setIsAbsent] = useState(false);
  const [totalMarks, setTotalMarks] = useState(100);

  // Progress tracking
  const [completedCount, setCompletedCount] = useState(0);
  const [totalStudents, setTotalStudents] = useState(0);
  
  // Bulk absent marking
  const [showBulkAbsentDialog, setShowBulkAbsentDialog] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const grades = [6, 7, 8, 9, 10, 11];
  const availableSubjects = ['Mathematics', 'Science'];

  useEffect(() => {
    fetchExams();
  }, []);

  useEffect(() => {
    if (selectedExam) {
      fetchExamSubjects();
    }
  }, [selectedExam]);

  useEffect(() => {
    if (selectedExam && selectedGrade && selectedSubject) {
      fetchStudents();
    }
  }, [selectedExam, selectedGrade, selectedSubject]);

  const fetchExams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/exams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExams(response.data);
    } catch (err) {
      setError('Failed to fetch exams');
    }
  };

  const fetchExamSubjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/exams/${selectedExam}/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubjects(response.data);
    } catch (err) {
      setError('Failed to fetch subjects');
    }
  };

  const fetchStudents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Find the exam_subject_id for the selected subject
      // Match subject name that includes "Mathematics" or "Science"
      const examSubject = subjects.find(s => {
        const subjectName = s.subject?.name || '';
        return subjectName.includes(selectedSubject);
      });
      
      if (!examSubject) {
        setError(`No ${selectedSubject} subject found for this exam. Please check exam subjects.`);
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/results/students-for-entry`,
        {
          exam_id: selectedExam,
          exam_subject_id: examSubject.id,
          grade: selectedGrade,
          subject: selectedSubject,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setStudents(response.data.students);
      setCompletedCount(response.data.completed);
      setTotalStudents(response.data.total);
      
      // Store exam_subject_id for result entry
      setSelectedExamSubjectId(examSubject.id);
      
      // Get total marks
      setTotalMarks(examSubject.total_marks);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (student) => {
    setSelectedStudent(student);
    setMarksObtained(student.marks_obtained || '');
    setIsAbsent(student.is_absent || false);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedStudent(null);
    setMarksObtained('');
    setIsAbsent(false);
    setError('');
  };

  const handleSaveResult = async () => {
    if (!isAbsent && (marksObtained === '' || marksObtained < 0 || marksObtained > totalMarks)) {
      setError(`Marks must be between 0 and ${totalMarks}`);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/results/store-result`,
        {
          student_id: selectedStudent.id,
          exam_id: selectedExam,
          exam_subject_id: selectedExamSubjectId,
          marks_obtained: isAbsent ? null : marksObtained,
          is_absent: isAbsent,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSuccess('Result saved successfully!');
      handleCloseModal();
      fetchStudents(); // Refresh the list
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save result');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAbsent = async () => {
    setBulkProcessing(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const pendingStudents = students.filter(s => !s.has_result);
      
      let successCount = 0;
      let failCount = 0;
      
      // Process each pending student
      for (const student of pendingStudents) {
        try {
          await axios.post(
            `${API_URL}/results/store-result`,
            {
              student_id: student.id,
              exam_id: selectedExam,
              exam_subject_id: selectedExamSubjectId,
              marks_obtained: null,
              is_absent: true,
            },
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          );
          successCount++;
        } catch (err) {
          failCount++;
        }
      }
      
      setShowBulkAbsentDialog(false);
      
      if (failCount === 0) {
        setSuccess(`Successfully marked ${successCount} student(s) as absent!`);
      } else {
        setSuccess(`Marked ${successCount} student(s) as absent. ${failCount} failed.`);
      }
      
      fetchStudents(); // Refresh the list
      setTimeout(() => setSuccess(''), 5000);
      
    } catch (err) {
      setError('Failed to process bulk absent marking');
    } finally {
      setBulkProcessing(false);
    }
  };

  const filteredStudents = students.filter(student =>
    student.student_index.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.name_with_initials.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const progressPercentage = totalStudents > 0 ? (completedCount / totalStudents) * 100 : 0;

  const BRAND = {
    a: '#667eea',
    b: '#764ba2',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    surface: 'rgba(255, 255, 255, 0.95)',
    stroke: 'rgba(255,255,255,0.18)',
    ink: '#0f172a',
    muted: '#64748b',
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: BRAND.gradient,
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="xl">
        {/* Modern Header */}
        <Box sx={{ 
          mb: 4, 
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          p: { xs: 3, md: 4 }, 
          borderRadius: 4,
          boxShadow: '0 18px 70px rgba(0,0,0,0.28)',
          position: 'relative',
          overflow: 'hidden',
          border: `1px solid ${BRAND.stroke}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: BRAND.gradient,
          }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Avatar sx={{ 
                  width: 56, 
                  height: 56, 
                  background: BRAND.gradient,
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)'
                }}>
                  <AssignmentIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h3" fontWeight="800" sx={{ 
                  background: BRAND.gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  Exam Results Entry
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                Select exam, subject, and class to enter student results
              </Typography>
            </Box>
          </Box>
        </Box>
        {/* Selection Controls */}
        <Paper sx={{ 
          p: { xs: 3, md: 4 },
          mb: 4,
          background: BRAND.surface,
          backdropFilter: 'blur(10px)',
          borderRadius: 3,
          boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
          border: `1px solid ${BRAND.stroke}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{
              width: 4,
              height: 32,
              background: BRAND.gradient,
              borderRadius: 2,
              mr: 2
            }} />
            <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
              Select Exam Details
            </Typography>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth sx={{
                '& .MuiInputLabel-root': {
                  color: BRAND.muted,
                  fontWeight: 600,
                  backgroundColor: '#fff',
                  px: 1,
                  zIndex: 1,
                  maxWidth: 'none',
                  overflow: 'visible',
                  textOverflow: 'clip',
                  '&.Mui-focused': {
                    color: BRAND.a,
                  },
                },
                minWidth: { xs: '100%', sm: 260 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: BRAND.a,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: BRAND.a,
                    borderWidth: 2,
                  },
                },
              }}>
                <InputLabel id="exam-select-label" shrink>
                  Select Exam
                </InputLabel>
                <Select
                  labelId="exam-select-label"
                  id="exam-select"
                  value={selectedExam}
                  onChange={(e) => {
                    setSelectedExam(e.target.value);
                    setSelectedGrade('');
                    setSelectedSubject('');
                    setStudents([]);
                  }}
                  displayEmpty
                  input={<OutlinedInput label="Select Exam" />}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {exams.map((exam) => (
                    <MenuItem key={exam.id} value={exam.id}>
                      {exam.name} - {exam.exam_date}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={!selectedExam} sx={{
                '& .MuiInputLabel-root': {
                  color: BRAND.muted,
                  fontWeight: 600,
                  backgroundColor: '#fff',
                  px: 1,
                  zIndex: 1,
                  maxWidth: 'none',
                  overflow: 'visible',
                  textOverflow: 'clip',
                  '&.Mui-focused': {
                    color: BRAND.a,
                  },
                  '&.Mui-disabled': {
                    color: '#cbd5e1',
                  },
                },
                minWidth: { xs: '100%', sm: 260 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: BRAND.a,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: BRAND.a,
                    borderWidth: 2,
                  },
                },
              }}>
                <InputLabel id="grade-select-label" shrink>
                  Select Grade
                </InputLabel>
                <Select
                  labelId="grade-select-label"
                  id="grade-select"
                  value={selectedGrade}
                  onChange={(e) => {
                    setSelectedGrade(e.target.value);
                    setSelectedSubject('');
                    setStudents([]);
                  }}
                  displayEmpty
                  input={<OutlinedInput label="Select Grade" />}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {grades.map((grade) => (
                    <MenuItem key={grade} value={grade}>
                      Grade {grade}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={!selectedGrade} sx={{
                '& .MuiInputLabel-root': {
                  color: BRAND.muted,
                  fontWeight: 600,
                  backgroundColor: '#fff',
                  px: 1,
                  zIndex: 1,
                  maxWidth: 'none',
                  overflow: 'visible',
                  textOverflow: 'clip',
                  '&.Mui-focused': {
                    color: BRAND.a,
                  },
                  '&.Mui-disabled': {
                    color: '#cbd5e1',
                  },
                },
                minWidth: { xs: '100%', sm: 260 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  '&:hover fieldset': {
                    borderColor: BRAND.a,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: BRAND.a,
                    borderWidth: 2,
                  },
                },
              }}>
                <InputLabel id="subject-select-label" shrink>
                  Select Subject
                </InputLabel>
                <Select
                  labelId="subject-select-label"
                  id="subject-select"
                  value={selectedSubject}
                  onChange={(e) => {
                    setSelectedSubject(e.target.value);
                    setStudents([]);
                  }}
                  displayEmpty
                  input={<OutlinedInput label="Select Subject" />}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {availableSubjects.map((subject) => (
                    <MenuItem key={subject} value={subject}>
                      {subject === 'Mathematics' ? '🔢 Mathematics' : '🔬 Science'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Visual confirmation when grade and subject are selected */}
          {selectedGrade && selectedSubject && (
            <Alert 
              severity="info" 
              sx={{ 
                mt: 3, 
                borderRadius: 2,
                backgroundColor: 'rgba(102, 126, 234, 0.05)',
                borderLeft: `4px solid ${BRAND.a}`,
                '& .MuiAlert-icon': {
                  color: BRAND.a
                }
              }}
            >
              <Typography component="div" variant="body1" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                📋 Entering results for: 
                <Chip 
                  label={`Grade ${selectedGrade}`} 
                  size="small" 
                  sx={{ 
                    fontWeight: 600,
                    backgroundColor: BRAND.a,
                    color: '#fff'
                  }}
                /> 
                <Chip 
                  label={selectedSubject === 'Mathematics' ? '🔢 Mathematics' : '🔬 Science'}
                  color="primary" 
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Typography>
            </Alert>
          )}
        </Paper>

        {/* Stats Cards */}
        {selectedGrade && selectedSubject && students.length > 0 && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ 
                background: BRAND.surface,
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                border: `1px solid ${BRAND.stroke}`,
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      bgcolor: alpha('#3b82f6', 0.14),
                      p: 1.5,
                      borderRadius: 2,
                      display: 'flex',
                    }}>
                      <SchoolIcon sx={{ color: '#3b82f6', fontSize: 32 }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" fontWeight="900" sx={{ color: BRAND.ink, letterSpacing: -0.5 }}>
                        {totalStudents}
                      </Typography>
                      <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                        Total Students
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Card sx={{ 
                background: BRAND.surface,
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                border: `1px solid ${BRAND.stroke}`,
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      bgcolor: alpha('#10b981', 0.14),
                      p: 1.5,
                      borderRadius: 2,
                      display: 'flex',
                    }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 32 }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" fontWeight="900" sx={{ color: BRAND.ink, letterSpacing: -0.5 }}>
                        {completedCount}
                      </Typography>
                      <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                        Completed
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <Card sx={{ 
                background: BRAND.surface,
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                border: `1px solid ${BRAND.stroke}`,
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      bgcolor: alpha('#f59e0b', 0.14),
                      p: 1.5,
                      borderRadius: 2,
                      display: 'flex',
                    }}>
                      <PendingActionsIcon sx={{ color: '#f59e0b', fontSize: 32 }} />
                    </Box>
                    <Box>
                      <Typography variant="h4" fontWeight="900" sx={{ color: BRAND.ink, letterSpacing: -0.5 }}>
                        {totalStudents - completedCount}
                      </Typography>
                      <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                        Pending
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Students Table */}
        {selectedGrade && selectedSubject && (
          <Paper sx={{ 
            p: { xs: 3, md: 4 },
            background: BRAND.surface,
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: '0 10px 36px rgba(0,0,0,0.14)',
            border: `1px solid ${BRAND.stroke}`,
          }}>
            {/* Progress Bar */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 4,
                    height: 32,
                    background: BRAND.gradient,
                    borderRadius: 2,
                  }} />
                  <Typography variant="h5" fontWeight="700" sx={{ color: BRAND.ink }}>
                    Results Entry Progress
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {(totalStudents - completedCount) > 0 && (
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={() => setShowBulkAbsentDialog(true)}
                      sx={{
                        borderColor: '#ef4444',
                        color: '#ef4444',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 2.5,
                        '&:hover': {
                          borderColor: '#dc2626',
                          bgcolor: 'rgba(239, 68, 68, 0.08)',
                        },
                      }}
                    >
                      Mark All Pending as Absent
                    </Button>
                  )}
                  <Chip 
                    label={`${Math.round(progressPercentage)}% Complete`}
                    sx={{ 
                      bgcolor: alpha(BRAND.a, 0.15),
                      color: BRAND.a,
                      fontWeight: 700,
                      fontSize: '1rem',
                      px: 2
                    }}
                  />
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progressPercentage}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  bgcolor: '#e2e8f0',
                  '& .MuiLinearProgress-bar': {
                    background: BRAND.gradient,
                    borderRadius: 6,
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                  {completedCount} of {totalStudents} students
                </Typography>
                <Typography variant="body2" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                  {totalStudents - completedCount} remaining
                </Typography>
              </Box>
            </Box>

            {/* Search Box */}
            <TextField
              fullWidth
              placeholder="Search by Student Index or Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: BRAND.muted }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#f8fafc',
                  '&:hover fieldset': {
                    borderColor: BRAND.a,
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                    '& fieldset': {
                      borderColor: BRAND.a,
                    },
                  },
                },
              }}
            />

            {/* Students Table */}
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <LinearProgress sx={{ 
                  mb: 3,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#e2e8f0',
                  '& .MuiLinearProgress-bar': {
                    background: BRAND.gradient,
                  },
                }} />
                <Typography variant="h6" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                  Loading students...
                </Typography>
              </Box>
            ) : (
              <TableContainer sx={{ 
                maxHeight: 600,
                borderRadius: 2,
                border: '1px solid #e2e8f0',
              }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        backgroundColor: '#f8fafc',
                        color: BRAND.ink,
                        fontSize: '0.875rem',
                        borderBottom: '2px solid #e2e8f0'
                      }}>
                        Student Index
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        backgroundColor: '#f8fafc',
                        color: BRAND.ink,
                        fontSize: '0.875rem',
                        borderBottom: '2px solid #e2e8f0'
                      }}>
                        Name
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        backgroundColor: '#f8fafc',
                        color: BRAND.ink,
                        fontSize: '0.875rem',
                        borderBottom: '2px solid #e2e8f0'
                      }}>
                        Status
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        backgroundColor: '#f8fafc',
                        color: BRAND.ink,
                        fontSize: '0.875rem',
                        borderBottom: '2px solid #e2e8f0'
                      }}>
                        Marks
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        backgroundColor: '#f8fafc',
                        color: BRAND.ink,
                        fontSize: '0.875rem',
                        borderBottom: '2px solid #e2e8f0'
                      }}>
                        Grade
                      </TableCell>
                      <TableCell sx={{ 
                        fontWeight: 700, 
                        backgroundColor: '#f8fafc',
                        color: BRAND.ink,
                        fontSize: '0.875rem',
                        borderBottom: '2px solid #e2e8f0'
                      }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStudents.map((student, index) => (
                      <TableRow
                        key={student.id}
                        sx={{
                          '&:hover': { 
                            backgroundColor: '#f8fafc',
                            transform: 'scale(1.002)',
                            transition: 'all 0.2s ease',
                          },
                          cursor: 'pointer',
                          backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: BRAND.ink }}>
                          {student.student_index}
                        </TableCell>
                        <TableCell sx={{ color: BRAND.ink }}>
                          {student.name_with_initials}
                        </TableCell>
                        <TableCell>
                          {student.has_result ? (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Completed"
                              sx={{
                                bgcolor: '#dcfce7',
                                color: '#16a34a',
                                fontWeight: 600,
                                border: 'none'
                              }}
                              size="small"
                            />
                          ) : (
                            <Chip
                              label="Pending"
                              sx={{
                                bgcolor: '#fef3c7',
                                color: '#d97706',
                                fontWeight: 600,
                                border: 'none'
                              }}
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {student.is_absent ? (
                            <Chip 
                              label="Absent" 
                              sx={{
                                bgcolor: '#fee2e2',
                                color: '#dc2626',
                                fontWeight: 600,
                                border: 'none'
                              }}
                              size="small" 
                            />
                          ) : (
                            <Typography sx={{ fontWeight: 600, color: BRAND.ink }}>
                              {student.marks_obtained || '-'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {student.result_grade && (
                            <Chip 
                              label={student.result_grade}
                              sx={{
                                bgcolor: alpha(BRAND.a, 0.15),
                                color: BRAND.a,
                                fontWeight: 700,
                                minWidth: 45
                              }}
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant={student.has_result ? 'outlined' : 'contained'}
                            size="small"
                            startIcon={student.has_result ? <EditIcon /> : null}
                            onClick={() => handleOpenModal(student)}
                            sx={{
                              minWidth: 120,
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: 2,
                              background: !student.has_result ? BRAND.gradient : 'transparent',
                              color: !student.has_result ? '#fff' : BRAND.a,
                              borderColor: BRAND.a,
                              boxShadow: !student.has_result ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                              '&:hover': {
                                background: !student.has_result
                                  ? 'linear-gradient(90deg, #5568d3 0%, #653a8b 100%)'
                                  : alpha(BRAND.a, 0.08),
                                boxShadow: !student.has_result ? '0 6px 16px rgba(102, 126, 234, 0.4)' : 'none',
                              },
                            }}
                          >
                            {student.has_result ? 'Edit' : 'Enter Marks'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {/* Bulk Absent Confirmation Dialog */}
        <Dialog
          open={showBulkAbsentDialog}
          onClose={() => !bulkProcessing && setShowBulkAbsentDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            },
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', 
            color: '#fff',
            py: 3,
            px: 4,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CancelIcon sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h5" fontWeight="700">
                  Confirm Bulk Absent
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.95, mt: 0.5 }}>
                  This action will mark multiple students as absent
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ px: 4, py: 4 }}>
            <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
              <Typography variant="body1" fontWeight="600" gutterBottom>
                You are about to mark <strong>{totalStudents - completedCount} student(s)</strong> as absent.
              </Typography>
              <Typography variant="body2">
                This will affect all students who don't have results entered yet. This action cannot be easily undone.
              </Typography>
            </Alert>
            
            <Box sx={{ 
              p: 2.5,
              bgcolor: '#fff7ed',
              borderRadius: 2,
              border: '1px solid #fed7aa'
            }}>
              <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#ea580c', mb: 1 }}>
                Details:
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                    Total Students
                  </Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ color: BRAND.ink }}>
                    {totalStudents}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                    Already Completed
                  </Typography>
                  <Typography variant="h6" fontWeight="700" sx={{ color: '#10b981' }}>
                    {completedCount}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                    Will be Marked Absent
                  </Typography>
                  <Typography variant="h5" fontWeight="900" sx={{ color: '#ef4444' }}>
                    {totalStudents - completedCount}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 4, py: 3, bgcolor: '#f8fafc' }}>
            <Button 
              onClick={() => setShowBulkAbsentDialog(false)}
              disabled={bulkProcessing}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: BRAND.muted,
                '&:hover': {
                  bgcolor: alpha(BRAND.muted, 0.08),
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkAbsent}
              variant="contained"
              disabled={bulkProcessing}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)',
                },
                '&:disabled': {
                  background: '#e2e8f0',
                  color: '#94a3b8',
                },
              }}
            >
              {bulkProcessing ? `Processing... (${totalStudents - completedCount})` : 'Yes, Mark All as Absent'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Marks Entry Modal */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            },
          }}
        >
          <DialogTitle sx={{ 
            background: BRAND.gradient, 
            color: '#fff',
            py: 3,
            px: 4,
          }}>
            <Box>
              <Typography variant="h5" fontWeight="700" sx={{ mb: 0.5 }}>
                Enter Marks
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95 }}>
                {selectedStudent?.name_with_initials}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ px: 4, py: 3 }}>
            <Box sx={{ 
              mb: 3,
              p: 2,
              bgcolor: '#f8fafc',
              borderRadius: 2,
              border: '1px solid #e2e8f0'
            }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                    Student Index
                  </Typography>
                  <Typography variant="body1" fontWeight="700" sx={{ color: BRAND.ink }}>
                    {selectedStudent?.student_index}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 600 }}>
                    Total Marks
                  </Typography>
                  <Typography variant="body1" fontWeight="700" sx={{ color: BRAND.ink }}>
                    {totalMarks}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, color: BRAND.ink, fontWeight: 600 }}>
                Attendance Status
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant={!isAbsent ? 'contained' : 'outlined'}
                  onClick={() => setIsAbsent(false)}
                  fullWidth
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    background: !isAbsent ? BRAND.gradient : 'transparent',
                    color: !isAbsent ? '#fff' : BRAND.a,
                    borderColor: BRAND.a,
                    boxShadow: !isAbsent ? '0 4px 12px rgba(102, 126, 234, 0.3)' : 'none',
                    '&:hover': {
                      background: !isAbsent 
                        ? 'linear-gradient(90deg, #5568d3 0%, #653a8b 100%)'
                        : alpha(BRAND.a, 0.08),
                    },
                  }}
                >
                  Present
                </Button>
                <Button
                  variant={isAbsent ? 'contained' : 'outlined'}
                  onClick={() => setIsAbsent(true)}
                  fullWidth
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    background: isAbsent ? '#ef4444' : 'transparent',
                    color: isAbsent ? '#fff' : '#ef4444',
                    borderColor: '#ef4444',
                    boxShadow: isAbsent ? '0 4px 12px rgba(239, 68, 68, 0.3)' : 'none',
                    '&:hover': {
                      background: isAbsent ? '#dc2626' : 'rgba(239, 68, 68, 0.08)',
                    },
                  }}
                >
                  Absent
                </Button>
              </Box>
            </FormControl>

            {!isAbsent && (
              <TextField
                fullWidth
                type="number"
                label="Marks Obtained"
                value={marksObtained}
                onChange={(e) => setMarksObtained(e.target.value)}
                inputProps={{ min: 0, max: totalMarks, step: 0.5 }}
                helperText={`Enter marks between 0 and ${totalMarks}`}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: BRAND.a,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: BRAND.a,
                    },
                  },
                }}
              />
            )}

            {isAbsent && (
              <Alert 
                severity="warning" 
                sx={{ 
                  borderRadius: 2,
                  border: '1px solid #fbbf24',
                  bgcolor: '#fffbeb'
                }}
              >
                Student will be marked as Absent with no marks recorded.
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 4, py: 3, bgcolor: '#f8fafc' }}>
            <Button 
              onClick={handleCloseModal} 
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                color: BRAND.muted,
                '&:hover': {
                  bgcolor: alpha(BRAND.muted, 0.08),
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveResult}
              variant="contained"
              disabled={loading || (!isAbsent && marksObtained === '')}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                borderRadius: 2,
                background: BRAND.gradient,
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #5568d3 0%, #653a8b 100%)',
                  boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                },
                '&:disabled': {
                  background: '#e2e8f0',
                  color: '#94a3b8',
                },
              }}
            >
              {loading ? 'Saving...' : 'Save Result'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default TeacherResultsEntry;
